import { useCanvasStore } from '@/stores/canvas'
import { useEditorStore } from '@/stores/editor'
import { applyAutofitToTextObject, applyMaxLinesAndVerticalOffset } from './textHelper'
import { Canvas, Control, FabricImage, FabricObject, Rect } from 'fabric'
import { syncContactShadow } from './shadowHelper'
import { showGlowLayer } from './imageHelper'
import { usePlacementStore } from '@/stores/placement'
import { generateFloorColor } from './colorHelper'
import { layerNames } from '@/constants/layers'
import { moveToBottom, moveToFront, renderChevronIcon } from './commonHelper'

export const loadCanvasData = (fabricCanvas: any, canvasJson: any, bgColor: string) => {
  const canvasStore = useCanvasStore()
  const editorStore = useEditorStore()
  return new Promise<void>((resolve) => {
    if (!canvasJson) {
      resolve()
      return
    }

    fabricCanvas
      .loadFromJSON(canvasJson)
      .then(async () => {
        let logoUrl = ''
        const layers = fabricCanvas.getObjects().reduce((acc: any, obj: any) => {
          acc[obj.layerName] = obj
          return acc
        }, {})

        const subheadlineObject = fabricCanvas
          .getObjects()
          .find((obj: any) => obj.layerName === 'SubHeadline')
        if (subheadlineObject) {
          subheadlineObject.styles = []
          if (canvasStore.rawCouponCode) {
            const couponCode = canvasStore.rawCouponCode.toUpperCase()
            const simulatedTextLines = subheadlineObject.text?.split('\n')
            const lineIndex = simulatedTextLines?.findIndex((line: any) =>
              line.includes(couponCode),
            )
            if (lineIndex != undefined && lineIndex != -1 && simulatedTextLines) {
              const currentLine = simulatedTextLines[lineIndex]
              const startCharIndex = currentLine.indexOf(couponCode)
              const endCharIndex = startCharIndex + couponCode.length - 1
              for (let i = startCharIndex; i <= endCharIndex; i++) {
                subheadlineObject.styles[lineIndex] = subheadlineObject.styles[lineIndex] || {}
                subheadlineObject.styles[lineIndex][i] = { fontWeight: 'bold' }
              }
            }
          }
        }
        const subheadlineLayer: any = Object.values(layers).find(
          (l: any) => l.layerName === 'SubHeadline',
        )
        Object.values(layers).forEach((layer: any) => {
          if (layer.layerName === 'Headline') {
            /**
             * Adjusts the headline text to allow up to 2 lines before applying autofit.
             *
             * Initially, maxLines is 1, so directly applying autofit shrinks the font size instead of breaking it into multiple lines.
             * This function first sets `maxLines` (up to 2), which allows the text to break naturally.
             * Then, it applies autofit to adjust font size within the allowed lines.
             * If the text expands to two lines, vertical offset is also applied to headline and subheadline
             * to maintain visual alignment after height change.
             */
            applyMaxLinesAndVerticalOffset(layer, subheadlineLayer, layer.previousHeight)
            editorStore.headline = layer.text
          } else if (layer.layerName === 'SubHeadline') {
            applyAutofitToTextObject(layer, layer.fontSize)
            editorStore.subheadline = layer.text
          }
          if (layer.layerName === 'Logo') {
            layer.set({
              selectable: false,
              evented: false,
            })
            if (layer.visible) {
              logoUrl = layer.src
            }
          }
          if (layer.layerName === 'Logo - Horizontal') {
            layer.set({
              selectable: false,
              evented: false,
            })
            if (layer.visible) {
              logoUrl = layer.src
            }
          }
          if (layer.layerName.includes('CTA')) {
            layer.set({
              shadow: {
                color: 'rgba(0,0,0,0.3)',
                blur: 10,
                offsetX: 4,
                offsetY: 4,
              },
            })
          }
          if (layer.layerName === layerNames.AIShadow) {
            layer.set({
              selectable: false,
              evented: false
            })
          }
        })
        fabricCanvas.getObjects().forEach((obj: any) => {
          if (obj.layerName?.toLowerCase().includes('product')) {
            obj.controls.moveToFrontControl = new Control({
              x: -0.5,
              y: -0.5,
              offsetY: -16,
              cursorStyle: 'pointer',
              mouseUpHandler: (eventData, transform) => {
                moveToFront(eventData, transform)
              },
              render: renderChevronIcon('up'),
            })
            obj.controls.moveToBottomControl = new Control({
              x: -0.5,
              y: -0.5,
              offsetY: -16,
              offsetX: 22,
              cursorStyle: 'pointer',
              mouseUpHandler: (eventData, transform) => {
                moveToBottom(eventData, transform)
              },
              render: renderChevronIcon('down'),
            })
          }
        })
        fabricCanvas
          .getObjects()
          .forEach((obj: FabricImage & { layerId?: string; layerName: string }) => {
            if (obj.layerName?.toLowerCase().includes('product')) {
              const productId = obj.layerId
              if (productId) {
                const shadow = fabricCanvas
                  .getObjects()
                  .find(
                    (o: { layerName: string; layerId: string | undefined }) =>
                      (o.layerName === 'Shadow' || o.layerName == layerNames.AIShadow) && o.layerId === productId,
                  )
                if (shadow) {
                  syncContactShadow(obj, shadow, fabricCanvas)
                }
              }
            }
          })

        await showGlowLayer(fabricCanvas, logoUrl, bgColor)
        fabricCanvas?.renderAll()
      })
      .catch(() => {
        console.error('Failed to load canvas!')
      })
      .finally(() => {
        resolve()
      })
  })
}

export const addDuoToneBackground = async (fabricCanvas: any, color: string, bgTexture: string) => {
  // Remove current canvas background
  removeDuoToneBackground(fabricCanvas)
  fabricCanvas.backgroundImage = null

  // Get floor edge and default ad size from config store
  const { useAdSizeConfigStore } = await import('@/stores/adSizeConfig')
  const adSizeStore = useAdSizeConfigStore()
  const FLOOR_EDGE = adSizeStore.floorEdge
  const defaultAdSize = adSizeStore.canvasSize

  const placementStore = usePlacementStore()
  const adSize = placementStore.selectedPlacement?.creativeSetGuidelines.ad_size ?? defaultAdSize

  const topShape = new Rect({
    left: 0,
    top: 0,
    width: adSize.width,
    height: FLOOR_EDGE,
    selectable: false,
    evented: false,
    fill: color,
    layerName: layerNames.BackgroundShape,
  })

  const floorColor = generateFloorColor(color)

  const bottomShape = new Rect({
    left: 0,
    top: FLOOR_EDGE,
    width: adSize.width,
    height: adSize.height - FLOOR_EDGE,
    selectable: false,
    evented: false,
    fill: floorColor,
    layerName: layerNames.BackgroundShape,
  })

  const backgroundTexture: any = await FabricImage.fromURL(bgTexture, {
    crossOrigin: 'anonymous',
  })

  backgroundTexture.set({
    left: 0,
    top: 0,
    selectable: false,
    evented: false,
    layerName: layerNames.BackgroundTexture,
    globalCompositeOperation: 'multiply',
  })

  fabricCanvas.add(backgroundTexture)
  fabricCanvas.add(topShape)
  fabricCanvas.add(bottomShape)

  fabricCanvas.moveObjectTo(backgroundTexture, 0)
  fabricCanvas.moveObjectTo(topShape, 0)
  fabricCanvas.moveObjectTo(bottomShape, 0)

  fabricCanvas.requestRenderAll()
}

export const removeDuoToneBackground = (fabricCanvas: any) => {
  const shapes = fabricCanvas
    .getObjects()
    .filter(
      (obj: any) =>
        obj.layerName == layerNames.BackgroundShape ||
        obj.layerName == layerNames.BackgroundTexture,
    )

  if (shapes.length) {
    shapes.forEach((shape: any) => {
      fabricCanvas.remove(shape)
    })
  }
}

export const updateCTA = (
  fabricCanvas: Canvas,
  canvasJson: any,
  isAllFoodCategories: boolean,
  textColor: string = '',
  primaryColor: string = '',
) => {
  const ctaLayer: any = fabricCanvas.getObjects().find((obj: any) => obj.layerName === 'CTA')
  const ctaOrderNowLayer: any = fabricCanvas
    .getObjects()
    .find((obj: any) => obj.layerName === 'CTA - Order now')

  const canvasCtaLayer: any = canvasJson.objects.find((obj: any) => obj.layerName === 'CTA')
  const canvasCtaOrderNowLayer: any = canvasJson.objects.find(
    (obj: any) => obj.layerName === 'CTA - Order now',
  )
  if (isAllFoodCategories) {
    if (ctaLayer) {
      ctaLayer.visible = false
      canvasCtaLayer.visible = false
    }
    if (ctaOrderNowLayer) {
      ctaOrderNowLayer.visible = true
      canvasCtaOrderNowLayer.visible = true
      if (textColor && primaryColor) {
        const innerText = canvasCtaOrderNowLayer.objects.find(
          (obj: FabricObject) => obj.type === 'Textbox',
        )
        if (innerText) {
          innerText.fill = textColor.toLowerCase() === '#ffffff' ? primaryColor : textColor
        }
      }
    }
  } else {
    if (ctaLayer) {
      ctaLayer.visible = true
      canvasCtaLayer.visible = true
      if (textColor && primaryColor) {
        const innerText = canvasCtaLayer.objects.find((obj: FabricObject) => obj.type === 'Textbox')
        if (innerText) {
          innerText.fill = textColor.toLowerCase() === '#ffffff' ? primaryColor : textColor
        }
      }
    }
    if (ctaOrderNowLayer) {
      ctaOrderNowLayer.visible = false
      canvasCtaOrderNowLayer.visible = false
    }
  }

  // Update inner text to "BUY NOW" because response from API is "Order Now"
  const innerText = canvasCtaLayer.objects.find((obj: FabricObject) => obj.type === 'Textbox')
  if (innerText) {
    innerText.text = 'BUY NOW'
  }
}
